<script setup lang="ts">
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import {
  setup,
  Props,
  Emit,
} from '@/common/components/molecules/timePeriod/timePeriodV2Calendar.setup';
import RangeCalendar from '@/common/components/molecules/rangeCalendar/RangeCalendar.vue';
import { stringifyDateString } from '@/common/components/molecules/timeController/utils';
import OneTimePicker from '../input/oneTimePicker/OneTimePicker.vue';

const props = withDefaults(defineProps<Props>(), {
  timezone: undefined,
});
const emit = defineEmits<Emit>();

const {
  t,
  rangeCalendarRef,
  startDate,
  endDate,
  startDateString,
  endDateString,
  onlyShowDate,
  timePickerValues,
  endTimeReadonlyTimes,
  timeValidationInfo,
  isRangeInToday,
  onClickResetDateTimePicker,
  onClickCancel,
  onClickOk,
  timePickerRef,
} = setup(props, emit);
</script>

<template>
  <div class="time-period-v2-calendar">
    <range-calendar
      ref="rangeCalendarRef"
      v-model:start-date="startDate"
      v-model:end-date="endDate"
      :disabled-selected-before-day="props.disabledSelectedBeforeDay"
      :disabled-today="false"
      :allow-future-dates="props.allowFutureDates"
    />
    <div class="time-period-v2-calendar__divider" />
    <div class="time-period-v2-calendar__notice-wrapper">
      <div
        v-if="props.maxRangeInSec"
        class="time-period-v2-calendar__notice"
      >
        <ev-icon icon="icon-calendar" />
        <p>
          {{ t('WORD.AVAILABLE_RANGE') }}
          <strong>{{ stringifyDateString(props.maxRangeInSec) }}</strong>
        </p>
      </div>
    </div>

    <div class="time-period-v2-calendar__time-picker-wrapper">
      <div class="time-period-v2-calendar__time-picker-item">
        <div class="time-period-v2-calendar__time-picker">
          <span class="time-period-v2-calendar__time-picker-label">
            {{ `${t('WORD.START')} (${t('WORD.OR_MORE')})` }}
          </span>
          <div class="time-period-v2-calendar__time-picker-date-wrapper">
            <span class="time-period-v2-calendar__time-picker-date">{{ startDateString }}</span>
            <one-time-picker
              v-if="!onlyShowDate"
              ref="timePickerRef"
              v-model="timePickerValues[0]"
              :error-msg="timeValidationInfo.fromTime"
              :readonly-times="props.readonlyTimes"
            />
          </div>
        </div>

        <span
          v-if="onlyShowDate && timeValidationInfo.fromTime"
          class="time-period-v2-calendar__error-msg"
        >
          {{ timeValidationInfo.fromTime }}
        </span>
      </div>

      <div class="time-period-v2-calendar__time-picker-item">
        <div class="time-period-v2-calendar__time-picker">
          <span class="time-period-v2-calendar__time-picker-label">
            {{ `${t('WORD.END')} (${t('WORD.LESS_THAN')})` }}
          </span>
          <div class="time-period-v2-calendar__time-picker-date-wrapper">
            <span
              v-if="endDateString"
              class="time-period-v2-calendar__time-picker-date"
              >{{ endDateString }}</span
            >
            <one-time-picker
              v-if="!onlyShowDate"
              v-model="timePickerValues[1]"
              :error-msg="timeValidationInfo.toTime"
              :readonly-times="endTimeReadonlyTimes"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="time-period-v2-calendar__button-wrapper">
      <base-button
        tag="button"
        type="button"
        :class="['time-period-v2-calendar__reset-button', { disabled: isRangeInToday }]"
        :disabled="isRangeInToday"
        @click="onClickResetDateTimePicker"
      >
        {{ t('WORD.RESET_TO_TODAY') }}
      </base-button>
      <div>
        <base-button
          type="default"
          size="small"
          @click.stop="onClickCancel"
        >
          {{ t('WORD.CANCEL') }}
        </base-button>
        <base-button
          type="primary"
          size="small"
          :disabled="!!timeValidationInfo.fromTime || !!timeValidationInfo.toTime"
          @click.stop="onClickOk"
        >
          {{ t('WORD.OK') }}
        </base-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.time-period-v2-calendar {
  display: flex;
  position: absolute;
  top: 100%;
  right: var(--time-period-select-width);
  z-index: $on-base-sub-menu;
  flex-direction: column;
  width: 528px;
  padding: 16px;
  margin: 4px 2px 0 0;
  border-radius: 2px;
  background-color: var(--color-gray-07-00);
  box-shadow: var(--dropdown-shadow);

  &__divider {
    width: calc(100% + 32px);
    height: 1px;
    margin: 0 -16px;
    margin-bottom: 16px;
    background-color: var(--color-gray-05-05);
  }

  .ev-calendar-date-area:not(:first-child) {
    border: none !important;
  }

  &__time-picker-wrapper {
    display: flex;
    justify-content: space-between;
    padding: 0 8px 8px;

    .time-period-v2-calendar__time-picker-item {
      flex: 1;
    }

    .time-period-v2-calendar__time-picker {
      display: flex;
      position: relative;
      flex: auto;
      flex-direction: column;
      align-items: flex-start;
      width: auto;
      padding-left: 12px;
      margin-bottom: 4px;

      &::before {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 4px;
        border-radius: 2px;
        background-color: var(--color-blue-05-05);
        content: '';
      }
    }

    .time-period-v2-calendar__time-picker-label {
      color: var(--color-gray-02-10);

      @include typography('body-3');
    }

    .time-period-v2-calendar__time-picker-date {
      display: flex;
      align-items: flex-start;
      margin-top: 8px;
      color: var(--main-font-color);

      @include typography('sub-title-1');

      &-wrapper {
        display: flex;
        gap: 8px;
        align-items: flex-start;
        justify-content: flex-start;
      }
    }

    .time-period-v2-calendar__error-msg {
      color: var(--color-red-06);

      @include typography('caption-1');
    }
  }

  .ev-input {
    height: 20px !important;
    color: var(--color-gray-02-10) !important;

    &.error {
      border-color: var(--color-red-05-05) !important;
    }
  }

  &__button-wrapper {
    display: flex;
    justify-content: space-between;

    .time-period-v2-calendar__reset-button {
      cursor: pointer;
      background-color: transparent;
      font-size: 10px;
      color: var(--color-blue-05-05);

      &.disabled {
        cursor: not-allowed;
        color: var(--color-gray-04-08);
      }
    }

    > div {
      display: flex;
      gap: 8px;
    }

    .ev-button {
      width: 60px !important;
    }
  }

  &__notice-wrapper {
    padding: 0 8px;
  }

  &__notice {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 12px;
    margin-bottom: 12px;
    border-radius: 4px;
    background-color: var(--color-gray-09-02);
    color: var(--main-font-color);
  }
}
</style>
